// =================================================================
// Govern360 - automatic Entra UPN capture (SaaS polish)
// Removes the last manual step: instead of hand-setting profiles.entra_upn,
// capture it from the Microsoft (Azure) sign-in automatically.
//
// Enforcement keys on the Entra UPN (e.g. raja@aivons.com), which often differs
// from the Govern360 login email. This populates it on every Azure SSO login.
// =================================================================

import { supabase } from './supabase.js';

// Pull the UPN from a Supabase user object populated by the Azure provider.
// Entra puts the UPN in preferred_username; we fall back through likely claims.
export function extractEntraUpn(user) {
  if (!user) return null;
  const md = user.user_metadata || {};
  // Most reliable first: Entra's preferred_username is the UPN for work accounts.
  const candidates = [
    md.preferred_username,
    md.upn,
    md.email,
    // Azure identity record (if present)
    ...(Array.isArray(user.identities)
      ? user.identities
          .filter((i) => i.provider === 'azure')
          .map((i) => i.identity_data?.preferred_username || i.identity_data?.upn || i.identity_data?.email)
      : []),
  ].filter(Boolean);
  // UPNs look like an email (user@domain). Take the first that does.
  const upn = candidates.find((c) => typeof c === 'string' && c.includes('@'));
  return upn ? upn.toLowerCase() : null;
}

// Capture + persist the UPN for the signed-in user, if not already set to it.
// Safe to call on every SIGNED_IN event; it no-ops when nothing changed.
// Relies on RLS allowing a user to update their OWN profile row.
export async function captureEntraUpn(user) {
  try {
    if (!user?.id) return { ok: false, reason: 'no-user' };

    // Only act on Azure sign-ins (don't clobber from a non-MS login).
    const isAzure =
      (user.app_metadata?.provider === 'azure') ||
      (Array.isArray(user.identities) && user.identities.some((i) => i.provider === 'azure'));
    if (!isAzure) return { ok: false, reason: 'not-azure' };

    const upn = extractEntraUpn(user);
    if (!upn) return { ok: false, reason: 'no-upn-claim' };

    // Read current value; only write if missing or different.
    const { data: prof } = await supabase
      .from('profiles')
      .select('entra_upn')
      .eq('id', user.id)
      .single();

    if (prof?.entra_upn && prof.entra_upn.toLowerCase() === upn) {
      return { ok: true, upn, changed: false };
    }

    const { error } = await supabase
      .from('profiles')
      .update({ entra_upn: upn })
      .eq('id', user.id);

    if (error) return { ok: false, reason: error.message };
    return { ok: true, upn, changed: true };
  } catch (e) {
    return { ok: false, reason: String(e?.message || e) };
  }
}
